/** @format */

import { dynamics365MarketingProvider } from './providers/dynamics365Marketing';
import type { CmsEmbedProvider, CmsEmbedWatchReadyContext } from './types';

const providers: CmsEmbedProvider[] = [dynamics365MarketingProvider];

export function resolveProvidersForContent({
  html = '',
  scriptSources = []
}: {
  html?: string;
  scriptSources?: string[];
} = {}): CmsEmbedProvider[] {
  const matched: CmsEmbedProvider[] = [];

  providers.forEach(provider => {
    const matchesHtml = provider.matchHtml?.(html);
    const matchesScript = scriptSources.some(src => provider.matchScript?.(src));
    if (matchesHtml || matchesScript) {
      matched.push(provider);
    }
  });

  return matched;
}

export function cleanupCmsEmbedProviders(activeProviders: CmsEmbedProvider[] = providers): void {
  activeProviders.forEach(provider => provider.cleanup?.());
}

export function shouldCacheBustScript(src: string, activeProviders: CmsEmbedProvider[] = providers): boolean {
  return activeProviders.some(
    provider => provider.matchScript?.(src) && provider.shouldCacheBust?.(src)
  );
}

export function providerWatchesReady(activeProviders: CmsEmbedProvider[]): boolean {
  return activeProviders.some(provider => typeof provider.watchReady === 'function');
}

export function subscribeProvidersReady(
  activeProviders: CmsEmbedProvider[],
  context: CmsEmbedWatchReadyContext
): Array<() => void> {
  return activeProviders
    .map(provider => provider.watchReady?.(context))
    .filter((fn): fn is () => void => typeof fn === 'function');
}

export function setupCmsEmbedPageEffects(
  html: string,
  pageContext: Record<string, unknown> = {}
): (() => void) | undefined {
  const activeProviders = resolveProvidersForContent({ html }).filter(
    provider => typeof provider.onEmbedReady === 'function'
  );

  if (!activeProviders.length) return undefined;

  const subscriptions = activeProviders.map(provider => {
    const eventName = provider.readyEventName;
    if (!eventName) return null;

    const handler = (event: Event) => provider.onEmbedReady?.({ event, ...pageContext });
    document.addEventListener(eventName, handler);
    return { eventName, handler };
  });

  return () => {
    subscriptions.forEach(subscription => {
      if (!subscription) return;
      document.removeEventListener(subscription.eventName, subscription.handler);
    });
  };
}
